import axios from 'axios';
import { chunk } from 'es-toolkit';
import qs from 'qs';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_LANG = 'en-us';
const RESULT_PAGED = true;
const RESULT_SINGLE = true;
const CUSTOM_QUESTION_CONCURRENCY = 8;

interface PrismicFetcherParams {
    lang?: string;
}

interface PrismicTypeFetcherArg {
    key: string;
    type: string;
    params?: PrismicFetcherParams;
}

interface PrismicTagFetcherArg {
    key: string;
    tag: string;
    params?: PrismicFetcherParams;
}

interface PrismicFilteredFetcherArg {
    key: string;
    filters: Record<string, unknown>;
    params?: PrismicFetcherParams;
}

interface PrismicTagsFetcherArg {
    key: string;
    tags: string[];
    params?: PrismicFetcherParams;
}

export const getCustomerContentType = async ({ key, type, params }: PrismicTypeFetcherArg) => {
    console.debug(`getCustomerContentType ${key} ${type} ${params?.lang}`);
    return await getByCustomerContent(undefined, type, params?.lang);
};

export const getCustomerContentTag = async ({ key, tag, params }: PrismicTagFetcherArg) => {
    console.debug(`getCustomerContentTag ${key} ${tag} ${params?.lang}`);
    return await getByCustomerContent(tag, undefined, params?.lang);
};

export const getCustomerContentByTag = async ({ key, tag, params }: PrismicTagFetcherArg) => {
    console.debug(`getCustomerContentByTag ${key} ${tag} ${params?.lang}`);
    return await getByCustomerContent(tag, undefined, params?.lang, undefined, undefined, RESULT_PAGED);
};

export const getCustomerContentSingle = async ({ key, type, params }: PrismicTypeFetcherArg) => {
    console.debug(`getCustomerContentSingle ${key} ${type} ${params?.lang}`);
    const results = await getByCustomerContent(
        undefined,
        type,
        params?.lang,
        undefined,
        undefined,
        RESULT_PAGED,
        RESULT_SINGLE
    );
    return results && results.length > 0 ? results[0] : undefined;
};

export const getCustomerContentFiltered = async ({ key, filters, params }: PrismicFilteredFetcherArg) => {
    console.debug(`getCustomerContentFiltered ${key} ${filters} ${params?.lang}`);
    return await getByCustomerContent(undefined, undefined, params?.lang, undefined, filters, RESULT_PAGED);
};

export const getCustomerContentTagsPaged = async ({ key, tags, params }: PrismicTagsFetcherArg) => {
    console.debug(`getCustomerContentTagsPaged ${key} ${tags} ${params?.lang}`);
    return await getByCustomerContent(undefined, undefined, params?.lang, tags, undefined, RESULT_PAGED);
};

interface PrismicRichTextSpan {
    text: string;
}

interface PrismicSubQuestionRef {
    id?: string;
}

export interface PrismicQuestionData {
    question_key?: PrismicRichTextSpan[];
    radio_subquestions?: Array<{ radio_subquestion: PrismicSubQuestionRef }>;
    multi_select_subquestions?: Array<{ multi_select_subquestion: PrismicSubQuestionRef }>;
    list_subquestions?: Array<{ list_subquestion: PrismicSubQuestionRef }>;
}

export interface PrismicQuestionDoc {
    id: string;
    tags?: string[];
    data?: PrismicQuestionData;
}

const CUSTOM_QUESTION_TYPE = 'custom_question';
const QUESTION_KEY_FIELD = 'my.custom_question.question_key';

type FilterValue = string | { fulltext: string };

const fetchCustomQuestions = async (
    filter: Record<string, FilterValue>,
    lang: string
): Promise<PrismicQuestionDoc[]> => {
    const docs: PrismicQuestionDoc[] | undefined = await getByCustomerContent(
        undefined,
        CUSTOM_QUESTION_TYPE,
        lang,
        undefined,
        filter
    );
    return docs ?? [];
};

const chunkedParallel = async <T, R>(items: T[], size: number, worker: (item: T) => Promise<R>): Promise<R[]> => {
    const results: R[] = [];
    for (const group of chunk(items, size)) {
        const batch = await Promise.all(group.map(worker));
        results.push(...batch);
    }
    return results;
};

const fetchCustomQuestionByKey = async (key: string, lang: string): Promise<PrismicQuestionDoc[]> => {
    const docs = await fetchCustomQuestions({ [QUESTION_KEY_FIELD]: { fulltext: key } }, lang);
    // Defensive: `fulltext` is tokenized (splits on whitespace and likely on hyphens),
    // so it can return docs that share enough tokens. Keep only exact key matches.
    return docs.filter((doc) => doc.data?.question_key?.[0]?.text === key);
};

export const getCustomQuestionsByKeys = async (keys: string[], lang: string): Promise<PrismicQuestionDoc[]> => {
    const batches = await chunkedParallel(keys, CUSTOM_QUESTION_CONCURRENCY, (key) =>
        fetchCustomQuestionByKey(key, lang)
    );
    return batches.flat();
};

export const getCustomQuestionsByIds = async (ids: string[], lang: string): Promise<PrismicQuestionDoc[]> => {
    const batches = await chunkedParallel(ids, CUSTOM_QUESTION_CONCURRENCY, (id) =>
        fetchCustomQuestions({ 'document.id': id }, lang)
    );
    return batches.flat();
};

export async function getTraverseCustomerDocuments(
    customerKey: string,
    tag: string,
    lang: string = DEFAULT_LANG
): Promise<TraversalResult> {
    const doc: any = await axios.get(
        `${import.meta.env.VITE_ADMIN_PUBLIC_CUSTOMERS_API_ENDPOINT}/customer-traversed-documents`,
        { params: { customerKey: customerKey, tag: tag, lang: lang } }
    );
    return doc.data.data;
}

export async function getByCustomerContent(
    tag?: string,
    type?: string,
    lang: string = DEFAULT_LANG,
    tags?: string[],
    filters?: any,
    usePages?: boolean,
    single: boolean = false
): Promise<any> {
    if (usePages) {
        return await handlePagedResults(type, tags, tag, lang, filters, single);
    }
    return await handleResult(type, tags, tag, lang, filters);
}

async function handleResult(
    type: string | undefined,
    tags: string[] | undefined,
    tag: string | undefined,
    lang: string,
    filters: any | undefined
) {
    const doc: any = await axios.get(`${import.meta.env.VITE_ADMIN_PUBLIC_CUSTOMERS_API_ENDPOINT}/content`, {
        params: {
            type: type,
            tag: tags || tag,
            filterJson: filters ? JSON.stringify(filters) : undefined,
            lang: lang,
        },
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        },
    });
    return doc.data.data;
}

async function handlePagedResults(
    type: string | undefined,
    tags: string[] | undefined,
    tag: string | undefined,
    lang: string,
    filters: any | undefined,
    single: boolean
) {
    // Paging because of :
    // The limit for payload size currently cannot be changed. From https://docs.aws.amazon.com/apigateway/latest/developerguide/limits.html:
    //   10MB payload size, which cannot be changed currently.
    //   If you want more customization, you should run your API on an actual server (e.g. with Amazon EC2).

    const result: TraversalResult = { success: true, documents: [], error: undefined };

    let page = 1;

    while (true) {
        const doc: any = await axios.get(`${import.meta.env.VITE_ADMIN_PUBLIC_CUSTOMERS_API_ENDPOINT}/content`, {
            params: {
                type: type,
                tag: tags || tag,
                lang: lang,
                filterJson: filters ? JSON.stringify(filters) : undefined,
                page: page++,
                pageSize: single ? 1 : DEFAULT_PAGE_SIZE,
                usePages: true,
            },
            paramsSerializer: function (params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
        });
        result.documents = result.documents.concat(doc.data.data.results);
        if (!doc.data.data.next_page) {
            break;
        }
    }

    return result.documents;
}

export interface TraversedDocument {
    id: string;
    type: string;
    tags: string[];
    data: Record<string, unknown>;
}

export interface TraversalResult {
    success: boolean;
    documents: TraversedDocument[];
    error?: string;
}

export interface Link {
    id: string;
    type: string;
    depth: number;
}

export interface TraversalState {
    visitedIds: Set<string>;
    documents: TraversedDocument[];
    linksToFollow: Link[];
}
